import type { SalesHeadlineMetrics } from '../../api/types';
import { METRIC_TIPS } from './salesMetricTips';
import {
  formatAvg,
  formatCount,
  formatPercent,
  getCountTrend,
  getRateTrend,
  weekDayLabel,
  type Trend,
} from './salesViewFormat';

function TrendMark({ trend }: { trend: Trend }) {
  if (trend.direction === 'new') {
    return <span className="sv-card-change sv-card-change-new">New</span>;
  }
  const glyph = trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '';
  return (
    <span className={`sv-card-change sv-card-change-${trend.direction}`}>
      {glyph} {trend.label}
    </span>
  );
}

/** "-12.0% (-3) vs LM"; a missing comparison stays a bare dash. */
function versus(trend: Trend, frame: string): Trend {
  return trend.label === '—' || trend.direction === 'new' ? trend : { ...trend, label: `${trend.label} vs ${frame}` };
}

interface TileProps {
  label: string;
  tip: string;
  mtd: string;
  ytd: string;
  vsLastMonth: Trend;
  vsLastYear: Trend;
  weeks: string;
}

function Tile({ label, tip, mtd, ytd, vsLastMonth, vsLastYear, weeks }: TileProps) {
  return (
    <div className="sv-card-kpi" title={tip}>
      <div className="sv-card-kpi-label">{label}</div>
      <div className="sv-card-kpi-pair">
        <span className="sv-card-kpi-cell"><em>MTD</em><span className="sv-card-kpi-value">{mtd}</span></span>
        <span className="sv-card-kpi-cell"><em>YTD</em><span className="sv-card-kpi-value">{ytd}</span></span>
      </div>
      <div className="sv-card-kpi-series">
        <TrendMark trend={versus(vsLastMonth, 'LM')} />
        <TrendMark trend={versus(vsLastYear, 'LY')} />
      </div>
      <div className="sv-card-kpi-weeks">{weeks}</div>
    </div>
  );
}

/**
 * Sold, Bought and Effectiveness on the same frames: MTD and YTD, trended against last month
 * (LM) and the same span last year (LY), with LW / TW / 4wk underneath in small type. One
 * component for the carousel card and the drawer, so the two can never show different numbers.
 */
export function SalesKpiTiles({ m }: { m: SalesHeadlineMetrics }) {
  const weekLabel = weekDayLabel(m.week_elapsed_fraction);
  const unitTip = (side: string, mtd: number | null | undefined, pace: number | null | undefined,
    ytd: number | null | undefined, pytd: number | null | undefined) =>
    `Units ${side}. Month to date ${formatCount(mtd)} against ${pace == null ? '—' : formatAvg(pace)} for last month `
    + `over the same number of weekdays (LM). Year to date ${formatCount(ytd)} against ${formatCount(pytd)} `
    + `over the same span last year (LY). The small line is last week, this week so far (${weekLabel}) and the 4-week average.`;

  return (
    <div className="sv-card-kpis">
      <Tile
        label="Sold"
        tip={unitTip('sold', m.sold_mtd, m.sold_pm_pace, m.sold_ytd, m.sold_pytd)}
        mtd={formatCount(m.sold_mtd)}
        ytd={formatCount(m.sold_ytd)}
        vsLastMonth={getCountTrend(m.sold_mtd, m.sold_pm_pace)}
        vsLastYear={getCountTrend(m.sold_ytd, m.sold_pytd)}
        weeks={`LW ${formatAvg(m.sold_last_week)} · TW ${formatAvg(m.sold_this_week)} · 4wk ${formatAvg(m.sold_w4_avg)}`}
      />
      <Tile
        label="Bought"
        tip={unitTip('bought', m.bought_mtd, m.bought_pm_pace, m.bought_ytd, m.bought_pytd)}
        mtd={formatCount(m.bought_mtd)}
        ytd={formatCount(m.bought_ytd)}
        vsLastMonth={getCountTrend(m.bought_mtd, m.bought_pm_pace)}
        vsLastYear={getCountTrend(m.bought_ytd, m.bought_pytd)}
        weeks={`LW ${formatAvg(m.bought_last_week)} · TW ${formatAvg(m.bought_this_week)} · 4wk ${formatAvg(m.bought_w4_avg)}`}
      />
      <Tile
        label="Effectiveness"
        tip={`${METRIC_TIPS.effectiveness} Month to date and year to date, against all of last month (LM) `
          + 'and the same span last year (LY), in points. The small line is last week, this week so far '
          + `(${weekLabel}) and the 4-week rate. A dash means nothing was listed.`}
        mtd={formatPercent(m.sell_through_mtd, 0)}
        ytd={formatPercent(m.sell_through_ytd, 0)}
        vsLastMonth={getRateTrend(m.sell_through_mtd, m.sell_through_pm)}
        vsLastYear={getRateTrend(m.sell_through_ytd, m.sell_through_pytd)}
        weeks={`LW ${formatPercent(m.sell_through_lw, 0)} · TW ${formatPercent(m.sell_through_tw, 0)}`
          + ` · 4wk ${formatPercent(m.sell_through_w4, 0)}`}
      />
    </div>
  );
}
